import * as primitives from "@mirage/service-typeahead-search/service/primitives";
import {
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import * as wrappers from "@mirage/service-typeahead-search/service/utils/wrappers";
import {
  ASK_DASH_UUID,
  ORGANIZE_DASH_UUID,
  SEARCH_DASH_UUID,
  WRITE_DASH_UUID,
} from "@mirage/shared/search/cache-result";
import * as rx from "rxjs";
import * as sourceScore from "../scoring/source-score";
import { doesNotContainFilterBindingPrefix } from "../utils/predicate-factory";
import { getResultSection } from "../utils/section-mapper";

const doesNotContainFilterBindingPrefixPredicate =
  doesNotContainFilterBindingPrefix();

export const raw: typeahead.TaggedSearch = (query, mode, { config }) => {
  return rx.defer(() => {
    if (!query.length && mode !== typeahead.Mode.Organize) return rx.of();

    if (!config.isOmniboxEnabled) {
      const taggedResult = primitives.suggestedQuery(
        SEARCH_DASH_UUID,
        {
          uuid: SEARCH_DASH_UUID,
          query,
        },
        {
          sectionId: getResultSection(
            typeahead.ResultType.SuggestedQuery,
            mode,
            query,
            config,
          ),
        },
      );
      const scoredResult = sourceScore.score(query, config)(taggedResult);
      return rx.of(scoredResult);
    }

    const suggestedQueries = [];

    if ([typeahead.Mode.Ask, typeahead.Mode.Default].includes(mode)) {
      const isNotDefaultModeWhileFiltersAreActive =
        mode !== typeahead.Mode.Default ||
        doesNotContainFilterBindingPrefixPredicate(query, mode, {
          config,
          cacheAnalysis: {
            hasRecentQueries: false,
          },
        });

      if (isNotDefaultModeWhileFiltersAreActive && !config.initialQuery) {
        const taggedResult = primitives.askUserQuery(
          ASK_DASH_UUID,
          {
            uuid: ASK_DASH_UUID,
            query,
          },
          {
            sectionId: getResultSection(
              typeahead.ResultType.AskUserQuery,
              mode,
              query,
              config,
            ),
          },
        );
        const scoredResult = sourceScore.score(query, config)(taggedResult);
        suggestedQueries.push(scoredResult);
      }
    }

    if ([typeahead.Mode.Find, typeahead.Mode.Default].includes(mode)) {
      const taggedResult = primitives.suggestedQuery(
        SEARCH_DASH_UUID,
        {
          uuid: SEARCH_DASH_UUID,
          query,
        },
        {
          sectionId: getResultSection(
            typeahead.ResultType.SuggestedQuery,
            mode,
            query,
            config,
          ),
        },
      );
      const scoredResult = sourceScore.score(query, config)(taggedResult);
      suggestedQueries.push(scoredResult);
    }

    if ([typeahead.Mode.Organize].includes(mode)) {
      const taggedResult = primitives.organizeUserQuery(
        ORGANIZE_DASH_UUID,
        {
          uuid: ORGANIZE_DASH_UUID,
          query,
        },
        {
          sectionId: getResultSection(
            typeahead.ResultType.OrganizeUserQuery,
            mode,
            query,
            config,
          ),
        },
      );
      const scoredResult = sourceScore.score(query, config)(taggedResult);
      suggestedQueries.push(scoredResult);
    }

    if (mode === typeahead.Mode.Write) {
      const taggedResult = primitives.writeUserQuery(
        WRITE_DASH_UUID,
        {
          uuid: WRITE_DASH_UUID,
          query,
        },
        {
          sectionId: getResultSection(
            typeahead.ResultType.WriteUserQuery,
            mode,
            query,
            config,
          ),
        },
      );
      const scoredResult = sourceScore.score(query, config)(taggedResult);
      suggestedQueries.push(scoredResult);
    }

    return rx.of(...suggestedQueries);
  });
};

export const search = wrappers.wrapped(SourceId.SuggestedQueries, raw);
